import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { getCustomizedToolsBar, smartImportBar } from '@bingads-webui-react/global-menu';
import { ScopeContext, $current } from '@bingads-webui-react/scope-service';
import { getCurrentScope, Scope } from '@bingads-webui-react/with-site-map';
import { ClientCenterCustomerService } from '@bingads-webui/clientcenter-customer-service';

const HideSmartPageEntityId = 14000;

const TopMenuComponent = /* istanbul ignore next */ () => null;

export const withUPConfig = (WrappedComponent) => {
  const HoC = (props) => {
    const permissions = useContext(PermissionsContext);
    const { scope } = useContext(ScopeContext);
    const { serverContext } = props;
    const {
      CurrentAccount: {
        AccountMode,
        CurrencyCode: currencyCode,
      } = {},
    } = serverContext || {};
    const {
      values: {
        aid,
        uid,
        campaignId,
        adGroupId,
      },
    } = scope;

    const isCustomerScope = getCurrentScope(campaignId, adGroupId, aid) === Scope.Customer;
    const smartPageUPEnabled = permissions.dynamic.SmartPageUPIntegration;
    const isUnifiedProductSubscriptionEnabled = permissions.dynamic.UnifiedProductSubscription;
    const isESCGoogleImportEnabled = !isCustomerScope
    && AccountMode === 2 // ACCOUNT_MODE.UNIFIED_SMART
    && permissions.dynamic.ESCGoogleImportPostSignup
    && !permissions.dynamic.UnifiedProductSubscription
    && currencyCode === 'USD';

    // for ESC Globalization, will hide SmartPage when hideSmartPageInESC in preference is true.
    const [hideSmartPage, setHideSmartPage] = useState(true);

    // create a new ccCustomerService if not exist in props.
    const ccBaseUrl = _.get(props.settingsContext, ['Urls', 'ClientCenterBaseUrl']);
    const newCCCustomerService = useMemo(() => new ClientCenterCustomerService({ ccBaseUrl }), [ccBaseUrl]);
    const ccCustomerService = (_.isFunction(props.ccCustomerService) && props.ccCustomerService()) || newCCCustomerService;

    // read from preference to decide whether to show SmartPage.
    useEffect(() => {
      if (ccCustomerService.getUserPreferences && uid) {
        ccCustomerService.getUserPreferences({
          uid,
          id: HideSmartPageEntityId,
        }).then((data) => {
          if (data && data.length > 0) {
            const preference = data[0].resultAsJsonString ?
              JSON.parse(data[0].resultAsJsonString) :
              null;

            if (preference && _.has(preference, 'hideSmartPageInESC') &&
              _.isBoolean(preference.hideSmartPageInESC)) {
              setHideSmartPage(preference.hideSmartPageInESC);
            } else {
              setHideSmartPage(false);
            }
          }
        }).catch(() => {
          setHideSmartPage(false);
        });
      } else {
        setHideSmartPage(false);
      }
    }, [ccBaseUrl, ccCustomerService, uid]);

    const hide = {
      ...props.hide,
      import: !isESCGoogleImportEnabled,
      reports: true,
      searchBar: true,
      mmc: true,
      myWebsite: props.hide.myWebsite || !smartPageUPEnabled || isCustomerScope || hideSmartPage,
      accountBreadcrumb: isUnifiedProductSubscriptionEnabled,
      assetlibrary: true,
      copilot: true,
    };
    const onLogoClick = useCallback(() => {
      scope.set({
        cid: $current,
        aid: $current,
        uid: $current,
      });

      // Don't specify a page, this should make page menu use the default landing page
      scope.save('/campaign/mca');
    }, [scope]);

    const customizedToolsBar = getCustomizedToolsBar({
      TopMenuComponent,
      siteMapRootId: 'setup',
    });

    return (<WrappedComponent
      {...props}
      CustomizedToolsBar={customizedToolsBar}
      CustomizedImportBar={smartImportBar}
      hide={hide}
      onLogoClick={onLogoClick}
    />);
  };

  HoC.displayName = `withUPConfig(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    settingsContext: PropTypes.shape({}).isRequired,
    serverContext: PropTypes.shape({}).isRequired,
    hide: PropTypes.shape({
      accountBreadcrumb: PropTypes.bool,
      searchBar: PropTypes.bool,
      customerSelector: PropTypes.bool,
      myWebsite: PropTypes.bool,
    }),
    globalMenuActivity: PropTypes.shape({}),
    account: PropTypes.shape({}),
  };

  HoC.defaultProps = {
    hide: {},
    globalMenuActivity: { trace: _.noop, error: _.noop },
    account: {},
  };

  return hoistNonReactStatics(HoC, WrappedComponent);
};
